import React from 'react';
import { useState, useContext, useEffect, useCallback } from 'react';
import {
  getOrderMaterialStatusRequest,
  getOrdersStatusAdmin,
  getMedias,
  searchOrderRequest,
  getAdvertisers,
  getAgencies,
} from '../../../api/Api';
import BreadCrumb from '../../../components/admin/Breadcrumb';
import Header from '../../../components/Header';
import { useLoadingContext } from '../../../contexts/LoadingContext';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { FILTERS } from '../../../constants';
import BulmaPagination from '../../../components/Pagination';
import dateUtils from '../../../utilities/dateUtils';
import AdminMissingUsersTable from '../../../components/tables/AdminMissingUsersTable';
import FilterTextInput from '../../../components/inputs/FilterTextInput';
import FromToDateInput from '../../../components/inputs/FromToDateInput';
import OrderNumberInput from '../../../components/inputs/OrderNumberInput';
import InputWithAutocomplete from '../../../components/inputs/InputWithAutocomplete';
import { useToasts } from 'react-toast-notifications';

const RESULTSLIMITPERPAGE = process.env.REACT_APP_API_RESULTS_LIMIT || 10;

// Filtros Vacios
const emptyFilters = {
  year: '',
  month: '',
  orderNumber: '',
  media: '',
  fromDate: '',
  toDate: '',
  product: '',
  clientOrderNumber: '',
  advertiser: '',
};

const AdminHomePage = () => {
  const [orders, setOrders] = useState([]);
  const [activeOrder, setActiveOrder] = useState({ order_number: '', users: [] });
  const [displayOverlay, setDisplayOverlay] = useState(false);
  const [filter, setFilter] = useState(emptyFilters);
  const [medias, setMedias] = useState([]);
  const [mediaSearchTerm, setMediaSearchTerm] = useState('');
  const [childMedias, setChildMedias] = useState([]);
  const [childMediaSearchTerm, setChildMediaSearchTerm] = useState('');
  const [advertisers, setAdvertisers] = useState([]);
  const [advertiserSearchTerm, setAdvertiserSearchTerm] = useState('');
  const [resultsFetched, setResultsFetched] = useState(false);
  const [toDate, setToDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [agencySearchTerm, setAgencySearchTerm] = useState('');
  const { profile, fetchProfileData, isLoadingProfile } = useContext(ProfileContext);
  const { addToast } = useToasts();
  const [badFromYear, setBadFromYear] = useState(false);
  const [badToYear, setBadToYear] = useState(false);

  const { setLoading } = useLoadingContext();

  const [pagination, setPagination] = useState({
    pages: 0,
    currentPage: 1,
    count: 0,
    limit: RESULTSLIMITPERPAGE,
  });

  // Fetch Data
  const fetchData = async (offset = 0) => {
    setLoading(true);
    const newFilters = { ...filter, ...pagination, offset };
    const json = await getOrdersStatusAdmin(newFilters);
    const { count } = json;
    setPagination({ ...pagination, count });
    setOrders(json.results);
    setLoading(false);
  };

  // Funcion disparada al hacer click en boton de orden (Setea informacion y autoriza open Modal)
  const onClickOrder = async (order) => {
    setLoading(true);
    const orderData = await getOrderMaterialStatusRequest(order.order.order_number);
    setLoading(false);
    setActiveOrder(orderData);
    setDisplayOverlay(true);
  };

  // Boton para ejecutar click orden (Funcion de arriba)
  const renderActionButton = (order) => {
    const { status } = order;
    const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
        case 'procesada':
          return 'is-success';
        case 'no hay adjuntos':
        case 'no procesada':
          return 'is-warning';
        case 'faltante':
          return 'is-danger';
        default:
          return 'is-info';
      }
    };

    return (
      <button
        className={`button is-small ${getStatusColor(status)} is-rounded size`}
        onClick={() => {
          onClickOrder(order);
        }}
        style={{
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <span className="icon is-small">
          <i className="fa fa-eye"></i>
        </span>
        <span>{status}</span>
      </button>
    );
  };

  // Modal de la orden modernizado
  const NotificationModal = () => {
    return (
      <div className={`modal ${displayOverlay ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={() => setDisplayOverlay(false)}></div>
        <div className="modal-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <header
            className="modal-card-head"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            <p className="modal-card-title has-text-weight-bold">
              <span className="icon-text">
                <span className="icon">
                  <i className="fa fa-file-text-o"></i>
                </span>
                <span>Orden: {activeOrder.order}</span>
              </span>
            </p>
            <button
              className="delete"
              aria-label="close"
              onClick={() => setDisplayOverlay(false)}
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            ></button>
          </header>
          <section className="modal-card-body" style={{ padding: '2rem' }}>
            <div
              className="table-container"
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <table className="table is-bordered is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th className="has-text-weight-bold">Usuario</th>
                    <th className="has-text-weight-bold">Medio</th>
                    <th className="has-text-weight-bold">Orden vista</th>
                    <th className="has-text-weight-bold">Material</th>
                    <th className="has-text-weight-bold">Material Adjunto existe</th>
                    <th className="has-text-weight-bold">Material Adjunto descargado</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOrder &&
                    activeOrder.materials_status &&
                    activeOrder.materials_status.map((material, index) => (
                      <tr key={`${index}`}>
                        <td className="has-text-weight-medium">
                          {material.user || 'No hay usuarios registrados para el medio'}
                        </td>
                        <td className="has-text-weight-medium">{material.media}</td>
                        <td>
                          <span
                            className={`tag ${material.seen ? 'is-success' : 'is-danger'} is-light`}
                          >
                            {material.seen ? 'Sí' : 'No'}
                          </span>
                        </td>
                        <td className="has-text-weight-medium">{material.description}</td>
                        <td>
                          <span
                            className={`tag ${
                              material.attachment_exists ? 'is-success' : 'is-danger'
                            } is-light`}
                          >
                            {material.attachment_exists ? 'Sí' : 'No'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`tag ${
                              material.downloaded ? 'is-success' : 'is-danger'
                            } is-light`}
                          >
                            {material.downloaded ? 'Sí' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {activeOrder?.missing_users && activeOrder?.missing_users.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <AdminMissingUsersTable missingUsers={activeOrder.missing_users} />
              </div>
            )}
          </section>
          <footer
            className="modal-card-foot"
            style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}
          >
            <button
              className="button is-primary is-medium"
              onClick={() => setDisplayOverlay(false)}
              style={{
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
              }}
            >
              <span className="icon">
                <i className="fa fa-times"></i>
              </span>
              <span>Cerrar</span>
            </button>
          </footer>
        </div>
      </div>
    );
  };

  // Generador de información con Estados de las Ordenes para contador Inicial
  const filterOrders = (filter) => {
    switch (filter) {
      case FILTERS.NOUSERS: {
        return orders.filter(({ users }) => users.length === 0);
      }
      case FILTERS.WITHATTACHMENTS: {
        return orders.filter(({ order }) =>
          order.order_attachments.find((adjunto) => adjunto && adjunto.is_from_order)
        );
      }
      case FILTERS.WITHNOATTACHMENTS: {
        return orders.filter(
          ({ order }) => order.order_attachments.filter((order) => order.is_from_order).length === 0
        );
      }
      case FILTERS.NOTSEEN: {
        return orders.filter(({ order, users }) => {
          const seenBy = order.seen_by.map((user) => user.id);
          const usersMap = users.map((user) => user.id);
          return seenBy.length === 0 || seenBy.every((user) => usersMap.includes(user));
        });
      }
      case FILTERS.NOTDOWNLOADED: {
        return orders.filter(({ order, users }) => {
          const downloadedBy = order.order_attachments.map((adjunto) =>
            adjunto.downloaded_by.map((user) => user.id)
          );
          const usersMap = users.map((user) => user.id);
          return downloadedBy.length === 0 || downloadedBy.every((user) => usersMap.includes(user));
        });
      }
      default: {
        return orders;
      }
    }
  };

  // Obtener Estado de Cada orden
  const getDetailsMessage = (notification) => {
    let noUsers = notification.has_missing_users;
    let noAttachments =
      notification.order.order_attachments.length === 0 ? 'No tiene archivos adjuntos' : '';
    if (noUsers && noAttachments) return 'Sin usuarios / Sin Archivos adjuntos';
    if (noUsers) return 'Incompleto';
    if (noAttachments) return noAttachments;
    return 'Completo';
  };

  // Funcion Obtener Medios para filtros
  const getRepeaters = (notification) => {
    const { child_medias } = notification;
    const childMediasList = child_medias.map((child) => child.name);
    const childMedias = childMediasList.join(', ');
    return childMedias || 'Sin repetidoras';
  };

  //Funcion para obtener Advertisers para filtros
  const fetchAdvertisers = useCallback(() => {
    getAdvertisers()
      .then(({ json }) => {
        const advertisersArray = json.map((advertiser) => advertiser.name);
        setAdvertisers(advertisersArray);
      })
      .catch((error) => {
        console.log('advertisers', error);
      });
  }, []);

  // useEffect para obtener data actualizada en caso de usar filtros
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      fetchProfileData();
      const { json: mediasJson } = await getMedias('parentMediasOnly=True');
      const { json: agenciesJson } = await getAgencies();
      const { json: afiliadosJson } = await getMedias('childMediasOnly=True');
      setChildMedias(afiliadosJson.map((media) => media.name));
      setAgencies(agenciesJson.map((agency) => agency.name));
      await fetchData();
      const mediasSuggestions = mediasJson.map((media) => media.name);
      setMedias(mediasSuggestions);
      setResultsFetched(true);
      setLoading(false);
    };
    fetchAdvertisers();
    fetchOrders();
  }, []);

  // useEffect para actualizar filtros al escuchar cambios
  useEffect(() => {
    const from = dateUtils.isValidDate(fromDate) ? fromDate : '';
    const to = dateUtils.isValidDate(toDate) ? toDate : '';
    setFilter({
      ...filter,
      media: mediaSearchTerm,
      fromDate: from,
      toDate: to,
      advertiser: advertiserSearchTerm,
      childMedia: childMediaSearchTerm,
      agency: agencySearchTerm,
    });
  }, [
    mediaSearchTerm,
    fromDate,
    toDate,
    advertiserSearchTerm,
    childMediaSearchTerm,
    agencySearchTerm,
  ]);

  // Función para limpiar filtros
  const clearFilters = async () => {
    setFilter(emptyFilters);
    setMediaSearchTerm('');
    setToDate('');
    setFromDate('');
    setAdvertiserSearchTerm('');
    setChildMediaSearchTerm('');
    setAgencySearchTerm('');
  };

  //Función Manejadora de filtros
  const handleOnChangeFilter = (event, field) => {
    const filterChange = { [field]: event.target.value };
    setFilter({ ...filter, ...filterChange });
  };

  // Función aplicadora de filtros y realiza busqueda
  const applyFilter = async () => {
    setLoading(true);
    if (new Date(filter.toDate) < new Date(filter.fromDate)) {
      addToast(
        'Fecha Vigencia Hasta no puede ser menor que Fecha Desde o el formato de fechas está incorrecto, por favor verifique.',
        {
          appearance: 'error',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        }
      );
      setLoading(false);
      return;
    }

    if (badFromYear || badToYear) {
      addToast(
        'Fecha Vigencia Hasta no puede ser menor que Fecha Desde o el formato de fechas está incorrecto, por favor verifique.',
        {
          appearance: 'error',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        }
      );
      setLoading(false);
      return;
    }
    await fetchData();
    setResultsFetched(true);
    setLoading(false);
  };

  // Función manejadora de value de Date para filtro
  const handleChangeDate = (event, field) => {
    const { value } = event.target;
    const year = parseInt(value.slice(0, 4));

    if (field === 'from') {
      if (year < 1970) {
        setBadFromYear(true);
        setFromDate(value);
      } else {
        setBadFromYear(false);
        setFromDate(value);
      }
    } else {
      if (year < 1970) {
        setBadToYear(true);
        setToDate(value);
      } else {
        setBadToYear(false);
        setToDate(value);
      }
    }
  };

  // Paginación Componente
  const Pagination = () => {
    const { pages, currentPage, count, limit } = pagination;
    return (
      <section className="section">
        <div className="columns is-centered">
          <div className="column is-half">
            <BulmaPagination
              pages={Math.ceil(count / limit)}
              currentPage={currentPage}
              onChange={(page) => onClickPageNumber(page)}
            />
          </div>
        </div>
      </section>
    );
  };

  // función para Click en numero de pagination
  const onClickPageNumber = async (page) => {
    setLoading(true);
    const { limit } = pagination;
    let offset;
    if (page === 1) {
      offset = 0;
    } else {
      offset = (page - 1) * limit;
    }
    await fetchData(offset);
    setPagination({ ...pagination, currentPage: page });
    setLoading(false);
  };

  if (isLoadingProfile) {
    return (
      <div className="section">
        <div className="container has-text-centered">
          <div className="box" style={{ borderRadius: '12px', padding: '3rem' }}>
            <span className="icon is-large has-text-primary">
              <i className="fa fa-spinner fa-pulse fa-3x"></i>
            </span>
            <p className="title is-4 mt-4">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <BreadCrumb activeSection={'Ordenes'} />

      {/* ✅ SECCIÓN DE FILTROS MODERNIZADA */}
      <section className="section">
        <div
          className="box"
          style={{
            margin: '1rem 0',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            padding: '2rem',
          }}
        >
          <div className="content">
            {/* Primera fila de filtros */}
            <div className="columns is-multiline mb-2" style={{ marginBottom: '1rem' }}>
              <div className="column is-3">
                <div className="field">
                  <OrderNumberInput
                    filter={filter}
                    onChangeYear={(event) => handleOnChangeFilter(event, 'year')}
                    onChangeMonth={(event) => handleOnChangeFilter(event, 'month')}
                    onChangeOrderNumber={(event) => handleOnChangeFilter(event, 'orderNumber')}
                    margin="is-10"
                  />
                </div>
              </div>

              <div className="column is-3">
                <div className="field">
                  <FromToDateInput
                    title="Vigencia Orden"
                    fromDate={fromDate}
                    toDate={toDate}
                    onChangeFrom={(event) => handleChangeDate(event, 'from')}
                    onChangeTo={(event) => handleChangeDate(event, 'to')}
                  />
                </div>
              </div>

              <div className="column is-3">
                <div className="field">
                  <InputWithAutocomplete
                    title="Agencia"
                    suggestions={agencies}
                    searchTerm={agencySearchTerm}
                    setSeachTerm={setAgencySearchTerm}
                  />
                </div>
              </div>

              <div className="column is-3">
                <div className="field">
                  <InputWithAutocomplete
                    title="Anunciante"
                    suggestions={advertisers}
                    searchTerm={advertiserSearchTerm}
                    setSeachTerm={setAdvertiserSearchTerm}
                  />
                </div>
              </div>
            </div>

            {/* Segunda fila - Solo para Admin: Medios */}
            {profile?.is_admin && (
              <div className="columns mb-2" style={{ marginBottom: '1rem' }}>
                <div className="column is-4">
                  <div className="field">
                    <InputWithAutocomplete
                      title="Medio Cabecera"
                      suggestions={medias}
                      searchTerm={mediaSearchTerm}
                      setSeachTerm={setMediaSearchTerm}
                    />
                  </div>
                </div>

                <div className="column is-2">
                  <div className="field">
                    <InputWithAutocomplete
                      title="Medio Afiliado"
                      suggestions={childMedias}
                      searchTerm={childMediaSearchTerm}
                      setSeachTerm={setChildMediaSearchTerm}
                      disabled={mediaSearchTerm && childMedias.length === 0}
                    />
                  </div>
                </div>

                <div className="column is-2">
                  <div className="field">
                    <FilterTextInput
                      title="Producto"
                      value={filter.product}
                      onChange={(event) => handleOnChangeFilter(event, 'product')}
                    />
                  </div>
                </div>

                <div className="column is-4">
                  <div className="field">
                    <FilterTextInput
                      title="Número de Orden Cliente"
                      value={filter.clientOrderNumber}
                      onChange={(event) => handleOnChangeFilter(event, 'clientOrderNumber')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* //Tercera fila - Número de orden cliente
            <div className="columns mb-2" style={{ marginBottom: '1rem' }}>
              <div className="column is-6">
                <div className="field">
                  <FilterTextInput
                    title="Número de Orden Cliente"
                    value={filter.clientOrderNumber}
                    onChange={(event) => handleOnChangeFilter(event, 'clientOrderNumber')}
                  />
                </div>
              </div>
              {!profile?.is_admin && (
                <div className="column is-6">
                  <div className="field">
                    <FilterTextInput
                      title="Producto"
                      value={filter.product}
                      onChange={(event) => handleOnChangeFilter(event, 'product')}
                    />
                  </div>
                </div>
              )}
            </div> */}

            {/* Botones de acción mejorados */}
            <div className="has-text-right" style={{ marginTop: '2rem' }}>
              <div className="field is-grouped is-grouped-right">
                <div className="control">
                  <button
                    className="button is-light is-normal"
                    onClick={() => clearFilters()}
                    style={{
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span className="icon">
                      <i className="fa fa-times-circle"></i>
                    </span>
                    <span>Limpiar filtros</span>
                  </button>
                </div>
                <div className="control">
                  <button
                    className="button is-primary is-normal"
                    onClick={applyFilter}
                    style={{
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
                    }}
                  >
                    <span className="icon">
                      <i className="fa fa-search"></i>
                    </span>
                    <span>Consultar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ SECCIÓN DE ESTADÍSTICAS MODERNIZADA */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div className="columns">
          <div className="column is-full">
            <div className="tile is-ancestor has-text-centered">
              <div className="tile is-parent">
                <article
                  className="tile is-child box"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div className="icon-text is-flex-direction-column">
                    <span className="icon is-large mb-2">
                      <i className="fa fa-file-text-o fa-2x"></i>
                    </span>
                    <p className="title has-text-white">{orders.length}</p>
                    <p className="subtitle has-text-white-ter">Órdenes Totales</p>
                  </div>
                </article>
              </div>

              <div className="tile is-parent">
                <article
                  className="tile is-child box"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div className="icon-text is-flex-direction-column">
                    <span className="icon is-large mb-2">
                      <i className="fa fa-eye-slash fa-2x"></i>
                    </span>
                    <p className="title has-text-white">{filterOrders(FILTERS.NOTSEEN).length}</p>
                    <p className="subtitle has-text-white-ter">Sin Ver</p>
                  </div>
                </article>
              </div>

              <div className="tile is-parent">
                <article
                  className="tile is-child box"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div className="icon-text is-flex-direction-column">
                    <span className="icon is-large mb-2">
                      <i className="fa fa-download fa-2x"></i>
                    </span>
                    <p className="title has-text-white">
                      {filterOrders(FILTERS.NOTDOWNLOADED).length}
                    </p>
                    <p className="subtitle has-text-white-ter">Sin Descargar</p>
                  </div>
                </article>
              </div>

              <div className="tile is-parent">
                <article
                  className="tile is-child box"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div className="icon-text is-flex-direction-column">
                    <span className="icon is-large mb-2">
                      <i className="fa fa-users fa-2x"></i>
                    </span>
                    <p className="title has-text-white">{filterOrders(FILTERS.NOUSERS).length}</p>
                    <p className="subtitle has-text-white-ter">Sin Usuarios</p>
                  </div>
                </article>
              </div>

              <div className="tile is-parent">
                <article
                  className="tile is-child box"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                    color: '#333',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div className="icon-text is-flex-direction-column">
                    <span className="icon is-large mb-2">
                      <i className="fa fa-paperclip fa-2x"></i>
                    </span>
                    <p className="title">{filterOrders(FILTERS.WITHATTACHMENTS).length}</p>
                    <p className="subtitle">Con Adjuntos</p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ TABLA DE ÓRDENES MODERNIZADA */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div className="columns">
          <div className="column is-full">
            <div
              className="box"
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                padding: '1rem',
              }}
            >
              <div
                className="table-container"
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <table className="table is-fullwidth is-striped is-hoverable has-text-centered">
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th className="has-text-weight-bold has-text-centered">
                        <span className="icon-text">
                          <span>Orden</span>
                        </span>
                      </th>
                      <th className="has-text-weight-bold has-text-centered">
                        <span className="icon-text">
                          <span>Estado</span>
                        </span>
                      </th>
                      {/* <th className="has-text-weight-bold has-text-centered">
                        <span className="icon-text">
                          <span>Detalles</span>
                        </span>
                      </th> */}
                      <th className="has-text-weight-bold has-text-centered">
                        <span className="icon-text">
                          <span>Medio</span>
                        </span>
                      </th>
                      <th className="has-text-weight-bold has-text-centered">
                        <span className="icon-text">
                          <span>Repetidora</span>
                        </span>
                      </th>
                      <th className="has-text-weight-bold has-text-centered">
                        <span className="icon-text">
                          <span>Acciones</span>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((notification, index) => {
                      const detailsMessage = getDetailsMessage(notification);
                      const isComplete = detailsMessage === 'Completo';

                      return (
                        <tr
                          key={notification.order.order_number}
                          className="hover-row"
                          style={{
                            transition: 'all 0.2s ease',
                            borderLeft: `4px solid ${isComplete ? '#48c78e' : '#ff7043'}`,
                          }}
                        >
                          <td className="is-vcentered">
                            <span className="tag is-primary is-light is-medium has-text-weight-bold">
                              {notification.order.order_number}
                            </span>
                          </td>
                          <td className="is-vcentered">{renderActionButton(notification)}</td>
                          {/* <td className="is-vcentered">
                            <span
                              className={`tag ${
                                isComplete ? 'is-success' : 'is-warning'
                              } is-bold has-text-weight-medium`}
                              title={detailsMessage}
                            >
                              {detailsMessage.length > 20
                                ? `${detailsMessage.substring(0, 20)}...`
                                : detailsMessage}
                            </span>
                          </td> */}
                          <td className="is-vcentered">
                            <span className="has-text-weight-medium has-text-dark">
                              {notification.order.media.name}
                            </span>
                          </td>
                          <td className="is-vcentered">
                            <span className="has-text-weight-medium has-text-grey-dark">
                              {getRepeaters(notification)}
                            </span>
                          </td>
                          <td className="is-vcentered">
                            <button
                              className="button is-info is-small is-rounded"
                              onClick={() => onClickOrder(notification)}
                              style={{
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              }}
                              title="Ver detalles completos"
                            >
                              <span className="icon is-medium">
                                <i className="fa fa-search-plus"></i>
                              </span>
                              <span>Ver más</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th className="has-text-weight-bold has-text-centered">Orden</th>
                      <th className="has-text-weight-bold has-text-centered">Estado</th>
                      <th className="has-text-weight-bold has-text-centered">Detalles</th>
                      <th className="has-text-weight-bold has-text-centered">Medio</th>
                      <th className="has-text-weight-bold has-text-centered">Repetidora</th>
                      <th className="has-text-weight-bold has-text-centered">Acciones</th>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {orders.length === 0 && resultsFetched && (
                <div className="has-text-centered" style={{ padding: '3rem' }}>
                  <span className="icon is-large has-text-grey-light">
                    <i className="fa fa-search fa-3x"></i>
                  </span>
                  <p className="title is-4 has-text-grey-light mt-4">No se encontraron órdenes</p>
                  <p className="subtitle has-text-grey">Intenta ajustar los filtros de búsqueda</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ✅ PAGINACIÓN */}
      <Pagination />

      {/* ✅ MODAL MODERNIZADO */}
      {activeOrder && <NotificationModal order={activeOrder} />}

      {/* ✅ ESTILOS CSS ADICIONALES */}
      <style jsx>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .tile.is-child.box:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .table th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          padding: 1rem 0.75rem;
          vertical-align: middle;
        }

        .table td {
          padding: 1rem 0.75rem;
          vertical-align: middle;
        }

        .table-container {
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .tag {
          font-weight: 600;
          border-radius: 20px;
        }

        .size {
          font-size: 0.9rem !important;
        }

        .box {
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .field .label {
          color: #4a4a4a;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .columns {
          margin-bottom: 0.5rem !important;
        }

        .column {
          padding: 0.5rem !important;
        }

        .modal-card {
          max-width: 90vw;
          width: 1200px;
        }

        .icon-text {
          align-items: center;
        }

        .is-flex-direction-column {
          flex-direction: column;
        }

        /* Animaciones suaves */
        .tile.is-child.box,
        .button,
        .hover-row {
          transition: all 0.3s ease;
        }

        /* Gradientes para las cards de estadísticas */
        .tile.is-child.box:nth-child(1) {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .tile.is-child.box:nth-child(2) {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .tile.is-child.box:nth-child(3) {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }

        .tile.is-child.box:nth-child(4) {
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        }

        .tile.is-child.box:nth-child(5) {
          background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
        }

        /* Responsive */
        @media screen and (max-width: 768px) {
          .columns {
            margin: 0 !important;
          }

          .column {
            padding: 0.4rem !important;
          }

          .field.is-grouped.is-grouped-right {
            justify-content: center;
          }

          .table-container {
            overflow-x: auto;
          }

          .modal-card {
            max-width: 95vw;
            width: auto;
            margin: 0 1rem;
          }

          .tile.is-ancestor {
            flex-direction: column;
          }

          .tile.is-parent {
            width: 100%;
            margin-bottom: 1rem;
          }
        }

        /* Mejoras en la accesibilidad */
        .button:focus,
        .input:focus,
        .select select:focus {
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
        }

        /* Estados de loading */
        .button.is-loading::after {
          border-color: transparent transparent currentColor currentColor;
        }
      `}</style>
    </div>
  );
};

export default AdminHomePage;
